/* Bright Data vault — API-token + dataset probe. */
import GenericVault from "./GenericVault";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plug, CheckCircle2, AlertTriangle, Loader2, Search, Sparkles, Globe } from "lucide-react";

/* Haal token/groups uit een geplakte MCP-URL (of accepteer een kaal token). */
const extractMcpToken = (raw) => {
    const m = (raw || "").match(/[?&]token=([^&\s]+)/);
    return m ? m[1] : (raw || "").trim();
};

const extractMcpGroups = (raw) => {
    const m = (raw || "").match(/[?&]groups=([^&\s]+)/);
    return m ? m[1] : "";
};

function BrightDataVault({ provider, cfg, onSaved }) {
    const { t } = useTranslation();
    const settings = cfg.settings || {};
    const [mcpInput, setMcpInput] = useState("");
    const [pro, setPro] = useState(!!settings.mcp_pro);
    const [saving, setSaving] = useState(false);
    const [probeBusy, setProbeBusy] = useState(null);   // "tools" | "search" | "scrape"
    const [probeResult, setProbeResult] = useState(null);
    const [probeError, setProbeError] = useState(null);
    const [scrapeUrl, setScrapeUrl] = useState("");

    const saveMcp = async (nextPro = pro) => {
        const token = extractMcpToken(mcpInput);
        if (token.includes("*")) {
            toast.error(t("integr.bd_mcp_masked_token"));
            return;
        }
        setSaving(true);
        try {
            const payload = { settings: { mcp_pro: nextPro } };
            const groups = extractMcpGroups(mcpInput);
            if (token) payload.settings.mcp_token = token;
            if (groups) payload.settings.mcp_groups = groups;
            await api.patch("/integrations/brightdata", payload);
            toast.success(t("integr.bd_mcp_saved"));
            setMcpInput(""); onSaved?.();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setSaving(false); }
    };

    const probe = async (action) => {
        if (action === "scrape" && !scrapeUrl.trim()) { toast.error(t("integr.bd_mcp_scrape_placeholder")); return; }
        setProbeBusy(action); setProbeError(null);
        try {
            const r = await api.post("/integrations/brightdata/mcp/probe", {
                action, query: undefined, url: action === "scrape" ? scrapeUrl.trim() : undefined,
            });
            setProbeResult(r.data);
            toast.success(t("integr.bd_mcp_probe_done", { ms: r.data.latency_ms }));
        } catch (e) {
            setProbeResult(null);
            setProbeError(e?.response?.data?.detail || t("integr.probe_failed"));
        } finally { setProbeBusy(null); }
    };

    const mcpConfigured = !!(settings.mcp_token_masked || cfg.configured);

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-[#4c8bf5]/20 bg-[#4c8bf5]/[0.04] p-4" data-testid="brightdata-hint">
                <div className="flex items-start gap-2.5">
                    <Sparkles size={16} className="text-[#4c8bf5] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#4c8bf5]">{t("integr.bd_hint_title")}</div>
                        <p className="text-[11px] text-[var(--jr-text-2)] mt-1 leading-relaxed">{t("integr.bd_hint")}</p>
                    </div>
                </div>
            </div>

            {/* REST API key (hunts) */}
            <GenericVault provider={provider} cfg={cfg} onSaved={onSaved} appBaseUrl="" />

            {/* MCP server (agent-tools) */}
            <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)]/40 p-4 space-y-3" data-testid="brightdata-mcp-panel">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#4c8bf5] inline-flex items-center gap-1.5">
                        <Plug size={12} /> {t("integr.bd_mcp_title")}
                        {settings.mcp_token_masked ? (
                            <span className="px-1.5 py-0.5 rounded bg-[#22c55e]/15 text-[#22c55e] normal-case tracking-normal"
                                data-testid="bd-mcp-token-badge">{t("integr.bd_mcp_token_set")} · {settings.mcp_token_masked.slice(-8)}</span>
                        ) : cfg.configured ? (
                            <span className="px-1.5 py-0.5 rounded bg-white/[0.05] text-[var(--jr-text-3)] normal-case tracking-normal">{t("integr.bd_mcp_uses_api_key")}</span>
                        ) : null}
                        {settings.mcp_groups && (
                            <span className="px-1.5 py-0.5 rounded bg-[#4c8bf5]/12 text-[#4c8bf5] normal-case tracking-normal"
                                data-testid="bd-mcp-groups-badge" title={i18n.t("integrations.mcp_groups_tip")}>groups: {settings.mcp_groups}</span>
                        )}
                    </div>
                    <p className="text-[11px] text-[var(--jr-text-3)] mt-1 leading-relaxed">{t("integr.bd_mcp_sub")}</p>
                </div>
                <div className="flex gap-2">
                    <input value={mcpInput} onChange={(e) => setMcpInput(e.target.value)}
                        placeholder={t("integr.bd_mcp_paste")} data-testid="bd-mcp-token-input"
                        className="flex-1 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#4c8bf5]" />
                    <button onClick={() => saveMcp()} disabled={saving || (!mcpInput.trim() && pro === !!settings.mcp_pro)}
                        data-testid="bd-mcp-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#4c8bf5] text-black text-sm font-medium hover:bg-[#4c8bf5]/85 disabled:opacity-50">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("common.save")}
                    </button>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="bd-mcp-pro-toggle">
                    <input type="checkbox" checked={pro}
                        onChange={(e) => { setPro(e.target.checked); }}
                        className="accent-[#4c8bf5]" />
                    <span className="text-xs text-white">{t("integr.bd_mcp_pro")}</span>
                    <span className="text-[10px] text-[var(--jr-text-3)]">· {t("integr.bd_mcp_pro_sub")}</span>
                </label>

                {/* Probes */}
                {mcpConfigured && (
                    <div className="pt-2 border-t border-white/8 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                            <button onClick={() => probe("tools")} disabled={!!probeBusy} data-testid="bd-mcp-probe-tools"
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border bg-[#4c8bf5]/10 text-[#4c8bf5] border-[#4c8bf5]/30 hover:bg-[#4c8bf5]/20 disabled:opacity-50">
                                {probeBusy === "tools" ? <Loader2 size={12} className="animate-spin" /> : <Plug size={12} />} {t("integr.bd_mcp_probe_tools")}
                            </button>
                            <button onClick={() => probe("search")} disabled={!!probeBusy} data-testid="bd-mcp-probe-search"
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border bg-[#4c8bf5]/10 text-[#4c8bf5] border-[#4c8bf5]/30 hover:bg-[#4c8bf5]/20 disabled:opacity-50">
                                {probeBusy === "search" ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} {t("integr.bd_mcp_probe_search")}
                            </button>
                            <div className="flex items-center gap-1.5">
                                <input value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)}
                                    placeholder={t("integr.bd_mcp_scrape_placeholder")} data-testid="bd-mcp-scrape-url"
                                    className="w-56 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] text-white outline-none focus:border-[#4c8bf5]" />
                                <button onClick={() => probe("scrape")} disabled={!!probeBusy} data-testid="bd-mcp-probe-scrape"
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border bg-[#4c8bf5]/10 text-[#4c8bf5] border-[#4c8bf5]/30 hover:bg-[#4c8bf5]/20 disabled:opacity-50">
                                    {probeBusy === "scrape" ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />} {t("integr.bd_mcp_probe_scrape")}
                                </button>
                            </div>
                        </div>

                        {probeError && (
                            <div className="rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/[0.08] p-3 text-xs inline-flex items-start gap-2"
                                data-testid="bd-mcp-error">
                                <AlertTriangle size={14} className="text-[#ef4444] mt-0.5 shrink-0" />
                                <span className="text-[var(--jr-text-2)]">{probeError}</span>
                            </div>
                        )}
                        {probeResult && (
                            <div className="space-y-2" data-testid="bd-mcp-result">
                                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)]">
                                    <CheckCircle2 size={12} className="text-[#22c55e]" />
                                    <span className="text-[#22c55e]">{probeResult.action}</span>
                                    <span>· {probeResult.latency_ms}ms{probeResult.pro ? " · pro" : ""}</span>
                                    {probeResult.action === "tools" && <span className="text-white">{t("integr.bd_mcp_tools_found", { n: probeResult.count })}</span>}
                                    {probeResult.action === "scrape" && <span className="text-white">{probeResult.chars} chars</span>}
                                </div>
                                {probeResult.action === "tools" ? (
                                    <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                                        {(probeResult.tools || []).map((tl) => (
                                            <span key={tl.name} title={tl.description}
                                                className="text-[10px] font-mono px-2 py-1 rounded bg-white/[0.04] border border-white/8 text-[var(--jr-text-2)]">
                                                {tl.name}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <pre className="rounded-lg border border-white/8 bg-black/30 p-3 text-[10px] text-[var(--jr-text-2)] overflow-x-auto max-h-48 whitespace-pre-wrap">
                                        {probeResult.text || JSON.stringify(probeResult.data, null, 2)}
                                    </pre>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default BrightDataVault;
